import {
    AnimationBaseController,
    AnimationTextStyle,
    IAnimationControllerConfiguration,
    Inject,
    ModelEvents,
    SoundType,
} from '@denishussar/html5-game-engine';
import { AnimationContents } from '../../../AnimationContents';
import { ButtonController, IButtonControllerOptions } from '../../Common/ButtonController';
import { LevelEvents } from '../../Constants/Events';
import { COIN, COIN56, COIN64, COIN70 } from '../../Constants/ImageConstants';
import { Sounds } from '../../Constants/Sounds';
import { LocalizationContents } from '../../LocalizationContents';
import { GameLogic } from '../../Logic/GameLogic';
import { AppStateEnum, GameModel } from '../../Models/GameModel';
import { SoundModel } from '../../Models/SoundModel';
import { resizeScreen } from '../../Utils/Resize';
import { thousandSpacing } from '../../Utils/StringRework';
import { SettingsButtonController } from '../Settings/SettingsButton';
import { Backend } from '../../Backend';

export interface ILevelEndOptions {
}

export class LevelEndController extends AnimationBaseController {
    $model: GameModel;

    private _width: number = 0;
    private _height: number = 0;

    private _itemIndex: number = 0;
    private _itemIndexMax: number = 3;
    private _itemsUnlockIndices: number[] = [0];

    private _shopItemIndex: number = 0;
    private _selectedShopItemIndex: number = 0;


    public constructor(
        @Inject private _options: ILevelEndOptions,
        @Inject private _gameLogic: GameLogic,
        @Inject private _modelEvents: ModelEvents,
        @Inject private soundLevels: SoundModel,
        @Inject private _backend: Backend,
    ) {
        super('GarageScreenController');
    }

    protected getConfiguration(): IAnimationControllerConfiguration {
        return <IAnimationControllerConfiguration>{
            animationContent: AnimationContents.END_SCREEN_JSON,
            libraryName: 'endscreen',
            sounds: [
                {
                    id: Sounds.PURCHASE,
                    type: SoundType.SFX,
                },
            ],
            childControllers: {
                btnGarage: {
                    instanceName: 'btn_garage',
                    controller: ButtonController,
                    model: this.$model,
                    options: <IButtonControllerOptions>{
                        onClick: () => {
                            this.$model.resetLevel();
                            this._gameLogic.setState(AppStateEnum.garage);
                        },
                    },
                },
                btnLoop: {
                    instanceName: 'btn_replay',
                    controller: ButtonController,
                    options: <IButtonControllerOptions>{
                        onClick: () => {
                            if (this.$model.currentPlayer.health < 100) {
                                this._modelEvents.dispatch(this.$model, LevelEvents.showRepair, true);
                                return;
                            }

                            this._gameLogic.resetLevel();
                            this._gameLogic.setState(AppStateEnum.game);
                        },
                    },
                },
                btnLeaderboard: {
                    instanceName: 'btn_leaderboard',
                    controller: ButtonController,
                    options: <IButtonControllerOptions>{
                        onClick: () => {
                            // this.$model.resetLevel();
                            if (this._backend.isEnabled()) {
                                this._gameLogic.setState(AppStateEnum.leaderboard);
                            }
                        },
                    },
                },

            },
            modelWatch: {
                coins: (value: number) => {
                    this.updateCoins();
                },
                playerDistance: (value: number) => {
                    this.setTextByQuery('DistanceTXT', value.toString());
                },
            },
            modelEvents: {},
        };
    }

    protected onInitialize(): void {
        if(this.$model.gameScreen) this.$model.gameScreen = null;
        this.$model.cars[0].health = 100;

        this._width = this.display.width;
        this._height = this.display.height;

        const btnLeaderboard = this.$childControllers.get('btnLeaderboard') as ButtonController;

        if (!this._backend.isEnabled()) {
            btnLeaderboard.alpha = 0.5;
            btnLeaderboard.disabled();
        }

        this.setTextByQuery('DistanceTXT', Math.floor(this.$model.playerDistance).toString());
        this.setTextByQuery('ScoreTXT', this.$model.levelCoins.toString() + '!');
        // this.setTextVerticalAlignByQuery("DistanceTXT", "baseline");
        this.setTextVerticalAlignByQuery('ScoreTXT', 'top');
        this.updateCoins();


        this.gotoAndStopByQuery('icons_cars_garage', this.$model.garage_selectedCar);
        this.gotoAndStopByQuery('icons_cars_garage.car', this.$model.currentPlayer.paint);


        this.addChildControllerAsync({
            controller: SettingsButtonController,
            model: this.$model,
        }, false, 'settingsButton');
        this.localise();


        this.setStyles();
    }

    protected onResize(width: number, height: number, scale: number): void {
        resizeScreen(width, height, scale, this);
    }

    protected onUpdate(frameRate: number): void {
    }

    private updateCoins(): void {
        let coins = this._gameLogic.getCoins();
        let k = '';
        while (coins > 99999) {
            coins /= 1000;
            k += 'k';
        }


        this.setTextByQuery('MoneyTXT', '<coin> </coin>    ' + thousandSpacing(Math.floor(coins)) + k + ' ');
        this.setTextVerticalAlignByQuery('MoneyTXT', 'middle');
    }

    private localise() {

        this.setTextByQuery('title_endscreen', LocalizationContents.getText('title_endscreen'));
        this.setTextByQuery('tracker_distance', LocalizationContents.getText('tracker_distance'));
        this.setTextByQuery('result.endscreen_gold', LocalizationContents.getText('endscreen_gold') + '  <coin></coin> <money>' + this.$model.levelCoins + '</money>');
        this.setTextVerticalAlignByQuery('title_endscreen', 'middle');
        this.setTextVerticalAlignByQuery('tracker_distance', 'middle');
        this.setTextVerticalAlignByQuery('result.endscreen_gold', 'middle');
    }

    private setStyles() {
        const coin64: AnimationTextStyle = {
            imgSrc: COIN64,
            height: 56,
            width: 58,
            valign: 'middle',
            padding: 10,
            fontSize: 64,
        };
        const coin70: AnimationTextStyle = {
            imgSrc: COIN70,
            height: 60,
            width: 70,
            valign: 'middle',
            padding: 10,
            fontSize: 70,
        };
        const coin56: AnimationTextStyle = {
            imgSrc: COIN56,
            height: 54,
            width: 56,
            valign: 'middle',
            padding: 10,
            fontSize: 70,
        };
        this.setTextStyleByQuery('result.endscreen_gold', {}, {
            progress: {
                fontFamily: 'Progress(RUS BY LYAJKA)',
            },
            coin: {
                imgSrc: COIN,
                height: 60,
                width: 77,
                valign: 'middle',
                padding: 10,
                fontSize: 77,
            },
            money: {
                fontFamily: 'Cimero Pro',
            },
        });
        this.setTextStyleByQuery('MoneyTXT', { valign: 'middle' }, {
            coin: coin64,
        });
    }
}
